import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  NativeSelect,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as yup from "yup";

import { useFormik } from "formik";
import { useEffect, useState } from "react";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Header from "../../components/Header";
import ModulePermissionsApi from "../../services/api/ModulePermissions";
import ProjectModuleApi from "../../services/api/ProjectModules";
import ProjectPermissionApi from "../../services/api/ProjectPermission";
import ActionApi from "../../services/api/actions";
import RoleApi from "../../services/api/roles";
import { tokens } from "../../theme";

const ModulePermissions = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isEdit, setIsEdit] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState();
  const [projectId, setProjectId] = useState();
  const [roleProjects, setRoleProjects] = useState([]);
  const [projectModules, setProjectModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [rolePermittedModules, setRolePermittedModules] = useState([]);

  useEffect(() => {
    fetchRoles();
    fetchActions();
  }, []);

  useEffect(() => {
    let modules = projectModules.map((module) => ({
      id: module.id,
      name: module.name,
      actions: actions.map((action, index) => {
        return findModuleActionHasPermission(module.id, index + 1);
      }),
    }));

    formik.setValues({
      ...formik.values,
      modules: modules,
    });
  }, [projectModules]);

  const findModuleActionHasPermission = (moduleId, actionId) => {
    const permitted = rolePermittedModules.find((module) => {
      return module.moduleId === moduleId && module.actionId === actionId;
    });

    return permitted ? true : false;
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: checkoutSchema,

    onSubmit: async (values, { resetForm }) => {
      console.log(values);
      createEntity(values);

      resetForm({ values: { ...initialValues, role_id: roleId } });
      fetchPermitedModules(roleId);
      handleAfterReset();
    },
  });

  const createEntity = async (values) => {
    const response = await ModulePermissionsApi().create(values);

    console.log(createdSuccessMsg, response);
    fetchRoleProjects(roleId);
  };

  const handleAfterReset = () => {
    setIsEdit(false);
  };

  const fetchRoles = async () => {
    try {
      const data = await RoleApi().getAll();

      setRoles(data.results);
    } catch (error) {
      console.log("Not loading roles", error);
    }
  };

  const fetchActions = async () => {
    try {
      const data = await ActionApi().getAll();

      setActions(data.results);
    } catch (error) {
      console.log("Not loading projects", error);
    }
  };

  const handleRoleChange = (event) => {
    setRoleId(event.target.value);
    fetchRoleProjects(event.target.value);
    fetchPermitedModules(event.target.value);
    formik.handleChange(event);
  };

  const handleProjectChange = (event) => {
    setProjectId(event.target.value);
    fetchProjectModules(event.target.value);
    formik.handleChange(event);
  };

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const destroy = (row) => {
    setSelectedId(row.id);
    handleOpenConfirmation();
  };

  const handleOpenConfirmation = () => {
    setConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
  };

  const handleConfirmAction = () => {
    try {
      const response = ProjectPermissionApi().destroy(selectedId);

      console.log(deletedSuccessMsg, response);
      fetchRoleProjects(roleId);
    } catch (error) {
      console.log(deleteErrorMsg, error);
    }

    handleCloseConfirmation();
  };

  const fetchRoleProjects = async (roleId) => {
    try {
      const data = await ProjectPermissionApi().getByRoleId(roleId);

      setRoleProjects(data);
    } catch (error) {
      setRoleProjects([]);
      if (error.response.status === 404) {
        console.log("This role has no projects.");
      } else {
        console.log("Loading error", error.response);
      }
    }
  };

  const fetchPermitedModules = async (roleId) => {
    try {
      const data = await ModulePermissionsApi().getByRoleId(roleId);

      setRolePermittedModules(data);
    } catch (error) {
      setRolePermittedModules([]);
      if (error.response.status === 404) {
        console.log("This role has no projects.");
      } else {
        console.log("Loading error", error.response);
      }
    }
  };

  const fetchProjectModules = async (projectId) => {
    try {
      const data = await ProjectModuleApi().getByProjectId(projectId);

      setProjectModules(data);
    } catch (error) {
      setProjectModules([]);
      if (error.response.status === 404) {
        console.log("This project has no modules.");
      } else {
        console.log("Loading error", error.response);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          padding: "15px 30px",
          borderBottom: "1px solid var(--color-primary-200)",
        }}
      >
        <Header title={`${formName}`} />
      </Box>

      <Box m="30px" display="flex" flexDirection="column" flex={1}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "15px",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, display: "flex", gap: 2 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={formik.values.role_id || ""}
                  onChange={handleRoleChange}
                  name="role_id"
                  displayEmpty // 👈 must be set to allow placeholder
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span className="placeholder-text">Select Role</span>
                      );
                    }
                    return roles?.find((r) => r.id === selected)?.name;
                  }}
                >
                  {/* The MenuItem for placeholder */}
                  <MenuItem value="" disabled>
                    Select Role
                  </MenuItem>

                  {roles?.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <Select
                  value={formik.values.project_id || ""}
                  onChange={handleProjectChange}
                  name="project_id"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span className="placeholder-text">Select Project</span>
                      );
                    }
                    return roleProjects?.find((p) => p.projectId === selected)
                      ?.projectName;
                  }}
                >
                  {/* Placeholder item in the dropdown */}
                  <MenuItem value="" disabled>
                    Select Project
                  </MenuItem>

                  {roleProjects?.map((project) => (
                    <MenuItem key={project.projectId} value={project.projectId}>
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Button
              type="submit"
              variant="contained"
              sx={{
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {isEdit ? "Update" : "Save"}
            </Button>
          </Box>

          <Box height="75vh">
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: "none",
                border: "1px solid var(--color-primary-100)",
                overflowX: "auto",
                height: "100%",
              }}
            >
              <Table
                sx={{
                  minWidth: 650,
                  "& .MuiTableRow-root.MuiTableRow-hover:hover": {
                    backgroundColor: "var(--color-primary-100)",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "var(--color-primary-100)",
                    }}
                  >
                    <TableCell>
                      <Typography variant="h6" color="secondary">
                        {"Module"}
                      </Typography>
                    </TableCell>
                    {formik.values.modules[0]?.actions.map((_, index) => (
                      <TableCell key={index}>
                        <Typography variant="h6" color="secondary">
                          {actions[index].name}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formik.values.modules.map((module, moduleIndex) => (
                    <TableRow
                      key={moduleIndex}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "& td": {
                          padding: "10px 16px",
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body1" color="secondary">
                          {module.name}
                        </Typography>
                      </TableCell>
                      {module.actions.map((action, actionIndex) => (
                        <TableCell key={actionIndex}>
                          <Checkbox
                            checked={action}
                            onChange={(e) => {
                              const newModules = [...formik.values.modules];
                              newModules[moduleIndex].actions[actionIndex] =
                                e.target.checked;
                              formik.setFieldValue("modules", newModules);
                            }}
                            inputProps={{ "aria-label": "controlled" }}
                            sx={{
                              color: "var(--color-primary-300)",
                              "&.Mui-checked": {
                                color: "var(--color-primary-400)",
                              },
                              "& .MuiSvgIcon-root": { fontSize: 28 },
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {formik.touched.modules && formik.errors.modules ? (
              <div className="error">{formik.errors.modules}</div>
            ) : null}
          </Box>
        </form>
      </Box>
      <ConfirmationDialog
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmAction}
      />
    </>
  );
};

const checkoutSchema = yup.object().shape({
  role_id: yup.number().required("required"),
  project_id: yup.number().required("required"),
  modules: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.number(),
        name: yup.string(),
        actions: yup.array().of(yup.boolean()),
      }),
    )
    .required("At least one module must be selected"),
});

const initialValues = {
  role_id: "",
  project_id: "",
  modules: [],
};

const formName = "Module Permission";
const createdSuccessMsg = `${formName} created:`;
const updatedSuccessMsg = `${formName} updated:`;
const deletedSuccessMsg = `${formName} deleted successfully: `;
const deleteErrorMsg = `Not deleted ${formName}`;

export default ModulePermissions;
